import TextField from 'lib/ui/TextField'
import Box from 'lib/ui/Box'
import Button from 'lib/ui/Button'
import Grid from 'lib/ui/Grid'
import Close from '@material-ui/icons/Close'
import ActionSelect from 'Event/ActionsProvider/ActionSelect'
import IconButton from 'lib/ui/IconButton'
import {Option} from 'organization/Event/QuestionsProvider'
import React from 'react'
import {Controller, useFieldArray} from 'react-hook-form'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'
import {DragHandle} from 'lib/ui/drag-and-drop'
import {QuestionTypeConfigProps} from 'organization/Event/Form/dialog/Form/QuestionTypeConfig'
import styled from 'styled-components'
import {typesWithOptions} from 'Event/Question'
import Label from 'lib/ui/TextField/Label'
import {useUserTranslations} from 'Event/LanguageProvider/translations'

export default function OptionsInput(props: QuestionTypeConfigProps) {
  const {control, register, errors} = props
  const v = useUserTranslations()
  const {fields, append, remove, move} = useFieldArray<Option>({
    control,
    name: 'options',
  })

  const hasOptions = typesWithOptions.includes(props.questionType)

  if (!hasOptions) {
    return null
  }

  const addOption = () => {
    append({
      value: '',
      action_key: null,
    })
  }

  const removeOption = (index: number) => () => {
    remove(index)
  }

  const handleDrag = (result: DropResult) => {
    const {destination, source} = result

    if (!destination) {
      return
    }

    move(source.index, destination.index)
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDrag}>
        <Droppable droppableId="question_options">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {fields.map((option, index) => {
                const error = errors?.options ? errors.options[index] : null
                const errorMessage = error?.value?.message

                return (
                  <Draggable
                    key={option.id}
                    draggableId={option.id || `option-${index}`}
                    index={index}
                  >
                    {(provided) => (
                      <div {...provided.draggableProps} ref={provided.innerRef}>
                        <Box>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Label>Option</Label>
                              <OptionTextField
                                variant="outlined"
                                name={`options[${index}].value`}
                                inputProps={{
                                  'aria-label': 'question option',
                                  ref: register({
                                    pattern: {
                                      value: /^[^,]+$/,
                                      message: 'Commas are not allowed.',
                                    },
                                  }),
                                }}
                                placeholder="Option"
                                defaultValue={v(option.value || '')}
                                fullWidth
                                error={Boolean(v(errorMessage || ''))}
                                helperText={v(errorMessage || '')}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-left"
                                height="100%"
                              >
                                <Controller
                                  control={control}
                                  name={`options[${index}].action_key`}
                                  defaultValue={option.action_key}
                                  render={({value, onChange}) => (
                                    <ActionSelect
                                      disableMargin
                                      value={value}
                                      onChange={onChange}
                                    />
                                  )}
                                />
                                <StyledDragHandle
                                  handleProps={provided.dragHandleProps}
                                  visible
                                />
                                <IconButton
                                  onClick={removeOption(index)}
                                  type="button"
                                  aria-label="remove option"
                                >
                                  <Close color="error" />
                                </IconButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </div>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Box mb={2} textAlign="right">
        <Button
          variant="outlined"
          onClick={addOption}
          color="primary"
          type="button"
          aria-label="add option"
        >
          Add Option
        </Button>
      </Box>
    </>
  )
}

const OptionTextField = styled(TextField)`
  font-size: 16px;
  height: 48px;
  margin: 0;
`

const StyledDragHandle = styled(DragHandle)`
  background: transparent;
  border-radius: 4px;
  margin-left: 8px;
  margin-right: 4px;
  cursor: grab;

  svg {
    color: ${(props) => props.theme.colors.text.muted};
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`
